/* eslint-disable @typescript-eslint/no-floating-promises, parog-web/no-glint-expect-error-directive */
import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import AnimatedContainer from 'ember-animated/components/animated-container';
import AnimatedEach from 'ember-animated/components/animated-each';
import move from 'ember-animated/motions/move';
import { fadeIn, fadeOut } from 'ember-animated/motions/opacity';
import type { EmberChangeset } from 'ember-changeset';
import type { BufferedChangeset } from 'ember-changeset/types';
import notEq from 'ember-truth-helpers/helpers/not-eq';

export interface ModelListItem {
  item: EmberChangeset;
  index: number;

  destroy: () => void;

  canMoveUp: boolean;
  moveUp: () => void;

  canMoveDown: boolean;
  moveDown: () => void;

  setDirty: () => void;
}

interface CommonArgs {
  createItem?: () => unknown;
  onDestroyItem?: (index: number) => void;

  listClass?: string;
  addButtonTitle?: string;
}

interface ChangesetBasedModelList extends CommonArgs {
  model: BufferedChangeset;
  propertyName: string;
}

interface ListBasedModelList extends CommonArgs {
  items: Array<unknown>;
  onUpdateItems: (items: Array<unknown>) => void;
}

export type ModelListArgs = ChangesetBasedModelList | ListBasedModelList;

export interface ModelListComponentSignature {
  Args: ModelListArgs;
  Blocks: {
    default: [ModelListItem];
  };
}

/**
 * This component helps handle list of things in a component.
 * This helper provides the logic for handling lists:
 * - adding an item,
 * - deleting one,
 * - moving items (coming later).
 *
 * The model is usually a changeset.
 */
export default class ModelListComponent<
  ActualModelListSignature extends ModelListComponentSignature
> extends Component<ActualModelListSignature> {
  get items(): ListBasedModelList['items'] {
    if (this.#isChangesetBased) {
      return this.#changesetBasedArgs.model.get(
        this.#changesetBasedArgs.propertyName
      );
    }
    return this.#listBasedArgs.items;
  }

  get lastItemIndex(): number {
    return Math.max(0, this.items.length - 1);
  }

  get #changesetBasedArgs(): ChangesetBasedModelList {
    return this.args as unknown as ChangesetBasedModelList;
  }

  get #isChangesetBased(): boolean {
    return Boolean((this.args as unknown as ChangesetBasedModelList).model);
  }

  get #listBasedArgs(): ListBasedModelList {
    return this.args as unknown as ListBasedModelList;
  }

  addItem = () => {
    this.addItemObject(this.#createItem());
  };

  destroyItem = (index: number) => {
    if (this.args.onDestroyItem) {
      this.args.onDestroyItem(index);
    }
    this.#updateList(list => {
      list.splice(index, 1);
    });
  };

  moveItemDown = index => {
    if (index == this.items.length - 1) return;

    this.#updateList(items => {
      const item = items[index];
      items.splice(index, 1);
      items.splice(index + 1, 0, item);
    });
  };

  moveItemUp = index => {
    if (index == 0) return;

    this.#updateList(items => {
      const item = items[index];
      items.splice(index, 1);
      items.splice(index - 1, 0, item);
    });
  };

  *transition({ insertedSprites, keptSprites, removedSprites }) {
    for (const sprite of insertedSprites) {
      fadeIn(sprite);
    }
    for (const sprite of keptSprites) {
      move(sprite);
    }
    for (const sprite of removedSprites) {
      fadeOut(sprite);
    }
    yield;
  }

  protected addItemObject(obj: unknown) {
    this.#updateList(list => {
      list.push(obj);
    });
  }

  /**
   * Calls the given constructor if it exists
   * or create an empty POJO.
   */
  #createItem() {
    return this.args.createItem ? this.args.createItem() : {};
  }

  #updateList(operation) {
    if (this.#isChangesetBased) {
      const items = this.#changesetBasedArgs.model
        .get(this.#changesetBasedArgs.propertyName)
        .slice(0);
      operation(items);
      this.#changesetBasedArgs.model.set(
        this.#changesetBasedArgs.propertyName,
        items
      );
    } else {
      const items = this.#listBasedArgs.items.slice();
      operation(items);
      this.#listBasedArgs.onUpdateItems(items);
    }
  }

  /**
   * this function is called to set dirty the changeset
   * when an elements inside items has changed.
   **/
  private setDirty = () => {
    const items = this.#changesetBasedArgs.model.get(
      this.#changesetBasedArgs.propertyName
    );
    this.#changesetBasedArgs.model.set(this.#changesetBasedArgs.propertyName, [
      ...items
    ]);
  };

  <template>
    <AnimatedContainer>
      <div class={{@listClass}}>
        {{#AnimatedEach
          this.items use=this.transition duration=300
          as |item index|
        }}
          {{! @glint-expect-error }}
          {{yield
            (hash
              item=item
              index=index
              destroy=(fn this.destroyItem index)
              canMoveUp=(notEq index 0)
              moveUp=(fn this.moveItemUp index)
              canMoveDown=(notEq index this.lastItemIndex)
              moveDown=(fn this.moveItemDown index)
              setDirty=(fn this.setDirty index)
            )
          }}
        {{/AnimatedEach}}
      </div>
    </AnimatedContainer>

    <button
      type='button'
      class='ui fluid labeled icon button [&.ui.button]:mt-6'
      data-test-button='add-item'
      {{on 'click' this.addItem}}
    >
      <i class='add icon'></i>
      {{if @addButtonTitle @addButtonTitle 'Ajouter'}}
    </button>
  </template>
}
